import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatDateTime } from '../../lib/datetime';
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  Calculator,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  FileText,
  GitBranch,
  ListChecks,
  MessageSquare,
  Pencil,
  Plus,
  RefreshCw,
  Sparkles,
  Trash2,
  UserPlus,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  PageHeader,
  Select,
  Spinner,
  Textarea,
  useToast,
  type SelectOption,
} from '../../ui';
import { useTaskDetail } from '../../features/sprintboard/useTaskDetail';
import {
  TASK_PRIORITIES,
  TASK_STATUSES,
  TASK_TYPES,
  boardStatusLabel,
  draftFromTask,
  priorityMeta,
  statusLabel,
  statusTone,
  totalEffortDays,
  validateTaskDraft,
} from '../../features/tasks/taskModel';
import type {
  BoardStatus,
  TaskDraft,
  TaskPriority,
  TaskStatus,
  TaskType,
} from '../../features/tasks/types';
import { formatDays } from '../../features/sprintboard/estimate/taskEstimateModel';
import { blockedForDays } from '../../features/sprintboard/models/sprintboardModel';
import { PeopleInput } from '../common/PeopleInput';
import { usePeopleDirectory } from '../../features/teams/usePeopleDirectory';
import { EstimateResultView } from './estimate/EstimateWizard';
import { EstimateFlowModal } from './estimate/EstimateFlowModal';
import { AddUpdateModal } from './AddUpdateModal';
import { AddTaskDocumentModal } from './AddTaskDocumentModal';
import { MarkBlockedModal } from '../Work/MarkBlockedModal';
import { MarkdownView } from '../Knowledge/MarkdownView';
import { CitationsPanel } from '../Chat/CitationsPanel';

const STATUS_OPTIONS: SelectOption[] = TASK_STATUSES.map((s) => ({
  value: s.value,
  label: s.label,
}));
const PRIORITY_OPTIONS: SelectOption[] = [
  { value: '', label: 'No priority' },
  ...TASK_PRIORITIES.map((p) => ({ value: p.value, label: p.label })),
];
const TYPE_OPTIONS: SelectOption[] = TASK_TYPES.map((t) => ({
  value: t,
  label: t,
}));

/** Badge tone per Sprint Board lane, for subtask status pills. */
const BOARD_TONE: Record<BoardStatus, 'info' | 'accent' | 'success'> = {
  TODO: 'info',
  IN_PROGRESS: 'accent',
  DONE: 'success',
};

/**
 * Inline editable "effort (days)" field for a subtask row. Keeps local text in
 * sync with the subtask's persisted effort and commits on blur / Enter.
 */
function SubtaskEffortInput({
  value,
  disabled,
  onCommit,
}: {
  value?: number;
  disabled?: boolean;
  onCommit: (raw: string) => void;
}) {
  const [text, setText] = useState(value != null ? String(value) : '');
  useEffect(() => {
    setText(value != null ? String(value) : '');
  }, [value]);
  return (
    <input
      type="number"
      min={0}
      step={0.5}
      disabled={disabled}
      value={text}
      placeholder="—"
      title="Custom effort (days)"
      aria-label="Subtask effort in days"
      onChange={(e) => setText(e.target.value)}
      onBlur={() => onCommit(text)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          (e.target as HTMLInputElement).blur();
        }
      }}
      className="h-7 w-14 shrink-0 rounded-lg border border-surface-border bg-surface-2 px-1.5 text-right text-xs tabular-nums text-zinc-100 focus:border-accent focus-visible:outline-none disabled:opacity-50"
    />
  );
}

/** A labeled read-only fact in the key line. */
function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
        {label}
      </span>
      <span className="text-sm text-zinc-200">{children}</span>
    </div>
  );
}

/**
 * Task page (`/tasks/:id`) — a clean, simple-on-the-surface view of one task:
 * the key facts, description, why/context, the engineering estimate block, the
 * OPTIONAL goal/workstream/service links, and the Updates / Blockers / Documents
 * sections. Advanced metadata is hidden by default behind a disclosure. Editing
 * the core fields happens inline; the estimate opens the compact flow.
 */
export function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { people } = usePeopleDirectory();

  const {
    task,
    estimate,
    sprint,
    goals,
    workstreams,
    services,
    updates,
    documents,
    subtasks,
    subtaskProgress,
    loading,
    error,
    refresh,
    saveTask,
    assignOwner,
    saveLinks,
    addUpdate,
    setBlocked,
    unblock,
    addDocument,
    addSubtask,
    cycleSubtask,
    updateSubtaskEffort,
    removeSubtask,
    researching,
    researchError,
    generateKbResearch,
  } = useTaskDetail(id);

  const [edit, setEdit] = useState<TaskDraft | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [savingLinks, setSavingLinks] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [estimateOpen, setEstimateOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [docOpen, setDocOpen] = useState(false);
  const [unblocking, setUnblocking] = useState(false);
  const [subtaskTitle, setSubtaskTitle] = useState('');
  const [subtaskOwner, setSubtaskOwner] = useState('');
  const [subtaskEffort, setSubtaskEffort] = useState('');
  const [subtaskPoints, setSubtaskPoints] = useState('');
  const [subtaskConfidence, setSubtaskConfidence] = useState('');
  const [addingSubtask, setAddingSubtask] = useState(false);
  const [cyclingId, setCyclingId] = useState<string | null>(null);
  const [showResearchQuestion, setShowResearchQuestion] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignAlias, setAssignAlias] = useState('');
  const [assigningOwner, setAssigningOwner] = useState(false);

  const goalOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: 'None' },
      ...goals.map((g) => ({ value: g.id, label: g.title || g.id })),
    ],
    [goals],
  );
  const workstreamOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: 'None' },
      ...workstreams.map((w) => ({ value: w.id, label: w.title || w.id })),
    ],
    [workstreams],
  );
  const serviceOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: 'None' },
      ...services.map((s) => ({ value: s.id, label: s.name || s.id })),
    ],
    [services],
  );

  const linkNames = useMemo(() => {
    if (!task) return {};
    return {
      goalName: goals.find((g) => g.id === task.goalId)?.title,
      workstreamName: workstreams.find((w) => w.id === task.workstreamId)?.title,
      serviceName: services.find((s) => s.id === task.serviceId)?.name,
    };
  }, [task, goals, workstreams, services]);

  // --- Loading / error / not-found -----------------------------------------
  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner label="Loading task" />
      </div>
    );
  }
  if (error || !task) {
    return (
      <div>
        <PageHeader
          icon={<ListChecks className="h-5 w-5" />}
          title="Task"
          description="Work item detail."
        />
        <EmptyState
          icon={<AlertTriangle className="h-6 w-6" />}
          title="Task not found"
          description={error ?? 'This task may have been deleted.'}
          action={
            <Button leftIcon={<ArrowLeft className="h-4 w-4" />} onClick={() => navigate('/tasks')}>
              Back to Tasks
            </Button>
          }
        />
      </div>
    );
  }

  const t = task; // narrowed
  const prio = priorityMeta(t.priority);

  const startEdit = () => setEdit(draftFromTask(t));
  const cancelEdit = () => setEdit(null);

  const commitEdit = async () => {
    if (!edit) return;
    const problems = validateTaskDraft(edit);
    if (problems.length > 0) {
      toast({ tone: 'error', title: 'Cannot save', description: problems[0] });
      return;
    }
    setSavingEdit(true);
    try {
      await saveTask(edit);
      toast({ tone: 'success', title: 'Task updated', description: edit.title });
      setEdit(null);
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Update failed',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setSavingEdit(false);
    }
  };

  const setLink = async (patch: {
    goalId?: string;
    workstreamId?: string;
    serviceId?: string;
  }) => {
    setSavingLinks(true);
    try {
      await saveLinks({
        goalId: patch.goalId ?? t.goalId ?? '',
        workstreamId: patch.workstreamId ?? t.workstreamId ?? '',
        serviceId: patch.serviceId ?? t.serviceId ?? '',
      });
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Couldn’t update link',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setSavingLinks(false);
    }
  };

  const setE = <K extends keyof TaskDraft>(key: K, value: TaskDraft[K]) =>
    setEdit((d) => (d ? { ...d, [key]: value } : d));

  const submitSubtask = async () => {
    const title = subtaskTitle.trim();
    if (!title) {
      toast({ tone: 'error', title: 'Cannot add', description: 'A subtask title is required.' });
      return;
    }
    // Parse the OPTIONAL inline estimate — blank stays undefined; invalid /
    // non-positive values are ignored so a subtask is always creatable.
    const parseOptionalNumber = (raw: string): number | undefined => {
      const trimmed = raw.trim();
      if (!trimmed) return undefined;
      const n = Number(trimmed);
      return Number.isFinite(n) && n >= 0 ? n : undefined;
    };
    const effortDays = parseOptionalNumber(subtaskEffort);
    const storyPoints = parseOptionalNumber(subtaskPoints);
    const confidence = parseOptionalNumber(subtaskConfidence);
    setAddingSubtask(true);
    try {
      await addSubtask({
        title,
        owner: subtaskOwner.trim() || undefined,
        effortDays,
        storyPoints,
        confidence,
      });
      setSubtaskTitle('');
      setSubtaskOwner('');
      setSubtaskEffort('');
      setSubtaskPoints('');
      setSubtaskConfidence('');
      toast({ tone: 'success', title: 'Subtask added', description: title });
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Couldn’t add subtask',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setAddingSubtask(false);
    }
  };

  const submitAssignOwner = async (alias: string) => {
    setAssigningOwner(true);
    try {
      await assignOwner(alias);
      setAssignOpen(false);
      toast({
        tone: 'success',
        title: alias.trim() ? 'Owner assigned' : 'Owner cleared',
        description: alias.trim() || 'Task is now unassigned.',
      });
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Couldn’t assign owner',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setAssigningOwner(false);
    }
  };

  const onCycleSubtask = async (subtask: typeof subtasks[number]) => {
    setCyclingId(subtask.id);
    try {
      await cycleSubtask(subtask);
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Couldn’t update subtask',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setCyclingId(null);
    }
  };

  const onRemoveSubtask = async (subtaskId: string, title: string) => {
    try {
      await removeSubtask(subtaskId);
      toast({ tone: 'success', title: 'Subtask removed', description: title });
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Couldn’t remove subtask',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    }
  };

  const onSubtaskEffort = async (
    subtask: typeof subtasks[number],
    raw: string,
  ) => {
    const trimmed = raw.trim();
    const n = trimmed === '' ? undefined : Number(trimmed);
    const effortDays =
      n != null && Number.isFinite(n) && n >= 0 ? n : undefined;
    if ((subtask.effortDays ?? undefined) === effortDays) return;
    try {
      await updateSubtaskEffort(subtask, effortDays);
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Couldn’t update effort',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    }
  };

  return (
    <div className="max-w-3xl">
      <button
        type="button"
        onClick={() => navigate('/tasks')}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-zinc-500 transition-colors hover:text-zinc-300"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Back to Tasks
      </button>

      {/* Title + key line */}
      <Card>
        <CardBody className="space-y-4">
          <div className="flex items-start justify-between gap-3">
            {edit ? (
              <Input
                label="Title"
                value={edit.title}
                onChange={(e) => setE('title', e.target.value)}
                className="text-base"
              />
            ) : (
              <h1 className="text-xl font-semibold tracking-tight text-zinc-50">
                {t.title}
              </h1>
            )}
            {!edit && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Pencil className="h-3.5 w-3.5" />}
                onClick={startEdit}
              >
                Edit
              </Button>
            )}
          </div>

          {edit ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Select
                label="Type"
                value={edit.type ?? 'Task'}
                options={TYPE_OPTIONS}
                onChange={(e) => setE('type', e.target.value as TaskType)}
              />
              <Select
                label="Status"
                value={edit.status}
                options={STATUS_OPTIONS}
                onChange={(e) => setE('status', e.target.value as TaskStatus)}
              />
              <Select
                label="Priority"
                value={edit.priority ?? ''}
                options={PRIORITY_OPTIONS}
                onChange={(e) =>
                  setE('priority', (e.target.value || undefined) as TaskPriority | undefined)
                }
              />
              <div className="sm:col-span-3">
                <PeopleInput
                  label="Owner (alias)"
                  value={edit.owner ?? ''}
                  people={people}
                  onChange={(alias) => setE('owner', alias)}
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Fact label="Type">
                <Badge tone="neutral">{t.type ?? 'Task'}</Badge>
              </Fact>
              <Fact label="Status">
                <span className="inline-flex items-center gap-1.5">
                  <Badge tone={statusTone(t.status)}>{statusLabel(t.status)}</Badge>
                  {t.blocked && <Badge tone="danger" dot>Blocked</Badge>}
                </span>
              </Fact>
              <Fact label="Owner">
                {assignOpen ? (
                  <div className="flex flex-col gap-2">
                    <PeopleInput
                      value={assignAlias}
                      people={people}
                      placeholder="alias…"
                      onChange={setAssignAlias}
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        variant="primary"
                        loading={assigningOwner}
                        leftIcon={<UserPlus className="h-3.5 w-3.5" />}
                        onClick={() => void submitAssignOwner(assignAlias)}
                      >
                        Assign
                      </Button>
                      {(t.owner || t.assignee) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={assigningOwner}
                          onClick={() => void submitAssignOwner('')}
                        >
                          Unassign
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={assigningOwner}
                        onClick={() => setAssignOpen(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <span className="inline-flex items-center gap-1.5">
                    {t.owner || t.assignee || (
                      <span className="text-zinc-600">Unassigned</span>
                    )}
                    <button
                      type="button"
                      title={t.owner || t.assignee ? 'Reassign owner' : 'Assign owner'}
                      onClick={() => {
                        setAssignAlias(t.owner || t.assignee || '');
                        setAssignOpen(true);
                      }}
                      className="text-zinc-500 transition-colors hover:text-zinc-300"
                    >
                      <UserPlus className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </span>
                )}
              </Fact>
              <Fact label="Sprint">{sprint?.name ?? <span className="text-zinc-600">Backlog</span>}</Fact>
              <Fact label="Priority">
                {prio ? <Badge tone={prio.tone} dot>{prio.label}</Badge> : <span className="text-zinc-600">—</span>}
              </Fact>
            </div>
          )}

          {edit && (
            <div className="flex items-center justify-end gap-2 border-t border-surface-border pt-3">
              <Button variant="ghost" onClick={cancelEdit} disabled={savingEdit}>
                Cancel
              </Button>
              <Button variant="primary" loading={savingEdit} onClick={commitEdit}>
                Save changes
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Description + Why/Context */}
      <Card className="mt-4">
        <CardBody className="space-y-4">
          {edit ? (
            <>
              <Textarea
                label="Description"
                rows={3}
                value={edit.description ?? ''}
                placeholder="What needs to happen, acceptance criteria, links…"
                onChange={(e) => setE('description', e.target.value)}
              />
              <Textarea
                label="Why / context"
                rows={2}
                value={edit.context ?? ''}
                placeholder="Why this matters / background."
                onChange={(e) => setE('context', e.target.value)}
              />
            </>
          ) : (
            <>
              <section>
                <h2 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-zinc-500">
                  Description
                </h2>
                {t.description ? (
                  <p className="whitespace-pre-wrap text-sm text-zinc-300">{t.description}</p>
                ) : (
                  <p className="text-sm text-zinc-600">No description yet.</p>
                )}
              </section>
              <section>
                <h2 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-zinc-500">
                  Why / context
                </h2>
                {t.context ? (
                  <p className="whitespace-pre-wrap text-sm text-zinc-300">{t.context}</p>
                ) : (
                  <p className="text-sm text-zinc-600">No context added yet.</p>
                )}
              </section>
            </>
          )}
        </CardBody>
      </Card>

      {/* AI Knowledge Base Research */}
      <Card className="mt-4">
        <CardHeader>
          <CardTitle>
            <span className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-accent" aria-hidden="true" />
              Knowledge Base Research
              <Badge tone="accent">AI</Badge>
            </span>
          </CardTitle>
          {t.kbResearch && (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
              loading={researching}
              onClick={() => void generateKbResearch()}
            >
              Refresh
            </Button>
          )}
        </CardHeader>
        <CardBody className="space-y-3">
          {researchError && (
            <div className="rounded-xl border border-red-500/30 bg-red-500/5 px-3 py-2 text-sm text-red-200">
              ⚠️ {researchError}
            </div>
          )}

          {t.kbResearch ? (
            <>
              <MarkdownView markdown={t.kbResearch.markdown} className="text-sm" />

              {t.kbResearch.citations.length > 0 && (
                <CitationsPanel citations={t.kbResearch.citations} />
              )}

              {/* Footer: AI disclaimer + model + timestamp + the question used */}
              <div className="space-y-1.5 border-t border-surface-border pt-3 text-xs text-zinc-500">
                <p className="italic text-zinc-500">
                  ✨ AI-generated from the team knowledge base — verify before
                  acting.
                </p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span>
                    Generated{' '}
                    {formatDateTime(t.kbResearch.generatedAt)}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setShowResearchQuestion((v) => !v)}
                  className="inline-flex items-center gap-1.5 font-medium text-zinc-500 hover:text-zinc-300"
                >
                  {showResearchQuestion ? (
                    <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                  {showResearchQuestion ? 'Hide question used' : 'Show question used'}
                </button>
                {showResearchQuestion && (
                  <p className="whitespace-pre-wrap rounded-lg border border-surface-border bg-surface-1 px-3 py-2 text-zinc-400">
                    {t.kbResearch.question}
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-start gap-3">
              <p className="text-sm text-zinc-500">
                Ask the team knowledge base what changes this task likely needs —
                grounded in our architecture docs, with cited sources.
              </p>
              <Button
                variant="primary"
                size="sm"
                leftIcon={<Sparkles className="h-3.5 w-3.5" />}
                loading={researching}
                onClick={() => void generateKbResearch()}
              >
                {researching ? 'Researching…' : 'Research with Vector KB'}
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Engineering estimate */}
      <Card className="mt-4">
        <CardHeader>
          <CardTitle>
            <span className="flex items-center gap-2">
              <Calculator className="h-4 w-4 text-accent" aria-hidden="true" />
              Engineering estimate
            </span>
          </CardTitle>
          {estimate ? (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Pencil className="h-3.5 w-3.5" />}
              onClick={() => setEstimateOpen(true)}
            >
              Edit estimate
            </Button>
          ) : (
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Sparkles className="h-3.5 w-3.5" />}
              onClick={() => setEstimateOpen(true)}
            >
              Estimate
            </Button>
          )}
        </CardHeader>
        <CardBody>
          {estimate ? (
            <EstimateResultView
              result={{
                breakdown: estimate.breakdown,
                engineeringEffortDays: estimate.effortDays,
                storyPoints: estimate.storyPoints,
                confidence: estimate.confidence,
                isSpike: estimate.isSpike,
                timeboxDays: estimate.timeboxDays,
              }}
              aiRationale={estimate.source === 'ai' ? estimate.rationale : undefined}
              source={estimate.source}
              override={estimate.override}
              kbSources={estimate.source === 'ai' ? estimate.sources : undefined}
            />
          ) : t.effortDays != null || t.storyPoints != null ? (
            // Mirrored numbers exist but the full estimate doc isn't loaded.
            <div className="grid grid-cols-3 gap-3">
              <MiniMetric label="Effort" value={t.effortDays != null ? formatDays(t.effortDays) : '—'} />
              <MiniMetric label="Story points" value={t.storyPoints != null ? String(t.storyPoints) : '—'} />
              <MiniMetric label="Confidence" value={t.confidence != null ? `${t.confidence}%` : '—'} />
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-zinc-500">
                Not estimated yet. Run the compact 4-step estimate, or let Vector suggest it.
              </p>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Subtasks */}
      <Card className="mt-4">
        <CardHeader>
          <CardTitle>
            <span className="flex items-center gap-2">
              <GitBranch className="h-4 w-4 text-accent" aria-hidden="true" />
              Subtasks
            </span>
          </CardTitle>
          {subtaskProgress.total > 0 && (
            <span className="text-xs tabular-nums text-zinc-400">
              {subtaskProgress.done}/{subtaskProgress.total} done
            </span>
          )}
        </CardHeader>
        {/* progress bar (thin) */}
        <CardBody className="space-y-4">
          <div className="flex items-center justify-between rounded-xl border border-surface-border bg-surface-1 px-3 py-2">
            <span className="text-xs font-medium text-zinc-400">
              Total Effort In Days
            </span>
            <span className="text-sm font-semibold tabular-nums text-zinc-100">
              {totalEffortDays(t, subtasks)}d
            </span>
          </div>
          {subtaskProgress.total > 0 && (
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-3">
              <div
                className="h-full rounded-full bg-accent"
                style={{
                  width: `${Math.round(
                    (subtaskProgress.done / subtaskProgress.total) * 100,
                  )}%`,
                }}
              />
            </div>
          )}

          {subtasks.length === 0 ? (
            <p className="text-sm text-zinc-600">
              No subtasks yet. Break this task into smaller steps below.
            </p>
          ) : (
            <ul className="space-y-2">
              {subtasks.map((s) => {
                const lane = (s.boardStatus ?? 'TODO') as BoardStatus;
                return (
                  <li
                    key={s.id}
                    className="flex items-center gap-2 rounded-xl border border-surface-border bg-surface-1 px-3 py-2"
                  >
                    <button
                      type="button"
                      onClick={() => void onCycleSubtask(s)}
                      disabled={cyclingId === s.id}
                      title="Cycle status: To Do → In Progress → Done"
                      className="shrink-0 disabled:opacity-50"
                    >
                      <Badge tone={BOARD_TONE[lane]} dot>
                        {boardStatusLabel(lane)}
                      </Badge>
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(`/tasks/${s.id}`)}
                      className="min-w-0 flex-1 truncate text-left text-sm text-zinc-200 hover:text-white"
                    >
                      {s.title}
                    </button>
                    {(() => {
                      const showSp =
                        typeof s.storyPoints === 'number' && s.storyPoints > 0;
                      return showSp ? (
                        <span className="shrink-0 text-xs tabular-nums text-zinc-500">
                          {s.storyPoints} SP
                        </span>
                      ) : null;
                    })()}
                    <SubtaskEffortInput
                      value={s.effortDays}
                      onCommit={(raw) => void onSubtaskEffort(s, raw)}
                    />
                    <span className="shrink-0 text-[11px] text-zinc-600">d</span>
                    {s.owner && (
                      <span className="inline-flex shrink-0 items-center gap-1 text-xs text-zinc-500">
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-surface-3 text-[10px] font-medium uppercase text-zinc-300">
                          {s.owner.slice(0, 2)}
                        </span>
                        {s.owner}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => void onRemoveSubtask(s.id, s.title)}
                      title="Remove subtask"
                      className="shrink-0 text-zinc-600 transition-colors hover:text-red-400"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Quick-create */}
          <div className="space-y-2 border-t border-surface-border pt-3">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
              <Input
                label="Add subtask"
                placeholder="What needs doing?"
                value={subtaskTitle}
                onChange={(e) => setSubtaskTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void submitSubtask();
                  }
                }}
              />
              <Button
                variant="secondary"
                leftIcon={<Plus className="h-3.5 w-3.5" />}
                loading={addingSubtask}
                onClick={() => void submitSubtask()}
              >
                Add
              </Button>
            </div>
            {/* Optional owner + compact estimate (all optional). */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <PeopleInput
                label="Owner"
                value={subtaskOwner}
                people={people}
                onChange={setSubtaskOwner}
              />
              <Input
                label="Effort (d)"
                type="number"
                inputMode="decimal"
                min={0}
                step={0.5}
                placeholder="—"
                value={subtaskEffort}
                onChange={(e) => setSubtaskEffort(e.target.value)}
              />
              <Input
                label="Story pts"
                type="number"
                inputMode="numeric"
                min={0}
                step={1}
                placeholder="—"
                value={subtaskPoints}
                onChange={(e) => setSubtaskPoints(e.target.value)}
              />
              <Input
                label="Confidence %"
                type="number"
                inputMode="numeric"
                min={0}
                max={100}
                step={5}
                placeholder="—"
                value={subtaskConfidence}
                onChange={(e) => setSubtaskConfidence(e.target.value)}
              />
            </div>
            <p className="text-xs text-zinc-600">
              Owner and estimate are optional — add a compact estimate now, or
              leave blank and estimate later.
            </p>
          </div>
        </CardBody>
      </Card>

      {/* Optional links */}
      <Card className="mt-4">
        <CardHeader>
          <CardTitle>Links</CardTitle>
          {savingLinks && <Spinner />}
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Select
              label="Goal"
              value={t.goalId ?? ''}
              options={goalOptions}
              disabled={savingLinks}
              onChange={(e) => void setLink({ goalId: e.target.value })}
            />
            <Select
              label="Workstream"
              value={t.workstreamId ?? ''}
              options={workstreamOptions}
              disabled={savingLinks}
              onChange={(e) => void setLink({ workstreamId: e.target.value })}
            />
            <Select
              label="Service"
              value={t.serviceId ?? ''}
              options={serviceOptions}
              disabled={savingLinks}
              onChange={(e) => void setLink({ serviceId: e.target.value })}
            />
          </div>
          <p className="mt-2 text-xs text-zinc-600">
            Optional — link this task to a goal, workstream, or service for context.
          </p>
        </CardBody>
      </Card>

      {/* Updates (power the Daily Scrum) */}
      <Card className="mt-4">
        <CardHeader>
          <CardTitle>
            <span className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-accent" aria-hidden="true" />
              Updates
            </span>
          </CardTitle>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Plus className="h-3.5 w-3.5" />}
            onClick={() => setUpdateOpen(true)}
          >
            Add Update
          </Button>
        </CardHeader>
        <CardBody>
          {updates.length === 0 ? (
            <p className="text-sm text-zinc-600">
              No updates yet. Task updates power the Daily Scrum.
            </p>
          ) : (
            <ul className="space-y-3">
              {updates.map((u) => (
                <li
                  key={u.id}
                  className="rounded-xl border border-surface-border bg-surface-1 px-3 py-2.5"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-zinc-200">
                      {u.author || '—'}
                    </span>
                    <span className="text-xs text-zinc-600">
                      {formatDateTime(u.createdAt, '')}
                    </span>
                  </div>
                  <dl className="mt-1.5 space-y-1 text-xs">
                    {u.completed && (
                      <div className="flex gap-1.5">
                        <dt className="w-16 shrink-0 font-medium text-emerald-400">
                          Completed
                        </dt>
                        <dd className="text-zinc-300">{u.completed}</dd>
                      </div>
                    )}
                    {u.next && (
                      <div className="flex gap-1.5">
                        <dt className="w-16 shrink-0 font-medium text-sky-400">Next</dt>
                        <dd className="text-zinc-300">{u.next}</dd>
                      </div>
                    )}
                    {u.blocker && (
                      <div className="flex gap-1.5">
                        <dt className="w-16 shrink-0 font-medium text-red-400">Blocker</dt>
                        <dd className="text-zinc-300">{u.blocker}</dd>
                      </div>
                    )}
                  </dl>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      {/* Blockers + Documents */}
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>
              <span className="flex items-center gap-2">
                <Ban className="h-4 w-4 text-accent" aria-hidden="true" />
                Blockers
              </span>
            </CardTitle>
            {t.blocked ? (
              <Button
                variant="ghost"
                size="sm"
                loading={unblocking}
                onClick={async () => {
                  setUnblocking(true);
                  try {
                    await unblock();
                    toast({ tone: 'success', title: 'Unblocked', description: t.title });
                  } catch (e) {
                    toast({
                      tone: 'error',
                      title: 'Couldn’t unblock',
                      description: e instanceof Error ? e.message : 'Unknown error',
                    });
                  } finally {
                    setUnblocking(false);
                  }
                }}
              >
                Unblock
              </Button>
            ) : (
              <Button variant="secondary" size="sm" onClick={() => setBlockOpen(true)}>
                Mark Blocked
              </Button>
            )}
          </CardHeader>
          <CardBody>
            {t.blocked && t.blocker ? (
              <div className="space-y-1.5">
                <p className="text-sm text-red-300">{t.blocker.reason}</p>
                {t.blocker.waitingOn && (
                  <p className="text-xs text-zinc-500">
                    Waiting on {t.blocker.waitingOn}
                  </p>
                )}
                {t.blocker.link && (
                  <a
                    href={t.blocker.link}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-accent-fg hover:underline"
                  >
                    <ExternalLink className="h-3 w-3" aria-hidden="true" />
                    Related link
                  </a>
                )}
                <p className="text-xs text-zinc-600">
                  Blocked for {blockedForDays(t.blocker.since)} day
                  {blockedForDays(t.blocker.since) === 1 ? '' : 's'}.
                </p>
              </div>
            ) : (
              <p className="text-sm text-zinc-600">Not blocked.</p>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              <span className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-accent" aria-hidden="true" />
                Documents
              </span>
            </CardTitle>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Plus className="h-3.5 w-3.5" />}
              onClick={() => setDocOpen(true)}
            >
              Add Document
            </Button>
          </CardHeader>
          <CardBody>
            {documents.length === 0 ? (
              <p className="text-sm text-zinc-600">No documents linked yet.</p>
            ) : (
              <ul className="space-y-2">
                {documents.map((d) => (
                  <li key={d.id} className="flex items-start justify-between gap-2">
                    <a
                      href={d.documentLink}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex min-w-0 items-center gap-1.5 text-sm text-zinc-200 hover:text-white"
                    >
                      <ExternalLink className="h-3.5 w-3.5 shrink-0 text-zinc-500" aria-hidden="true" />
                      <span className="truncate">{d.title}</span>
                    </a>
                    <Badge tone="neutral">{d.docType}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Advanced metadata (hidden by default) */}
      <div className="mt-4">
        <button
          type="button"
          onClick={() => setShowDetails((v) => !v)}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-500 hover:text-zinc-300"
        >
          {showDetails ? (
            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {showDetails ? 'Hide details' : 'Show details'}
        </button>
        {showDetails && (
          <div className="mt-2 grid grid-cols-1 gap-3 rounded-xl border border-surface-border bg-surface-1 px-4 py-3 text-xs text-zinc-500 sm:grid-cols-2">
            <div>Task id: <span className="text-zinc-400">{t.id}</span></div>
            <div>Created by: <span className="text-zinc-400">{t.createdBy || '—'}</span></div>
            <div>Updated: <span className="text-zinc-400">{formatDateTime(t.updatedAt)}</span></div>
            <div>
              Tags:{' '}
              {(t.tags ?? []).length > 0 ? (
                <span className="text-zinc-400">{(t.tags ?? []).join(', ')}</span>
              ) : (
                <span className="text-zinc-600">none</span>
              )}
            </div>
          </div>
        )}
      </div>

      <EstimateFlowModal
        open={estimateOpen}
        onClose={() => setEstimateOpen(false)}
        task={t}
        existingEstimate={estimate}
        goalName={linkNames.goalName}
        workstreamName={linkNames.workstreamName}
        serviceName={linkNames.serviceName}
        onSaved={() => void refresh()}
      />

      <AddUpdateModal
        open={updateOpen}
        onClose={() => setUpdateOpen(false)}
        taskId={t.id}
        taskTitle={t.title}
        onAdd={addUpdate}
      />

      <MarkBlockedModal
        open={blockOpen}
        onClose={() => setBlockOpen(false)}
        task={t}
        onBlock={(_task, blocker) => setBlocked(blocker).then(() => undefined)}
      />

      <AddTaskDocumentModal
        open={docOpen}
        onClose={() => setDocOpen(false)}
        taskTitle={t.title}
        onAdd={addDocument}
      />
    </div>
  );
}

function MiniMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-surface-border bg-surface-1 px-3 py-2.5 text-center">
      <p className="text-lg font-semibold tabular-nums text-zinc-100">{value}</p>
      <p className="mt-0.5 text-xs text-zinc-500">{label}</p>
    </div>
  );
}

export default TaskDetailPage;
